import React, { useState, useEffect, useCallback } from 'react';
import { 
  Layout,
  Card,
  InlineStack, 
  BlockStack,
  Select,
  Banner,
  Spinner,
  Pagination,
  Text,
  Button
} from '@shopify/polaris';
import { rentalsAPI } from '../services/api';
import RentalModal from '../components/RentalModal';
import RentalDetailModal from '../components/RentalDetailModal';
import RentalOverviewCard from '../components/RentalOverviewCard';
import RentalFulfillmentControl from '../components/RentalFulfillmentControl';
import RentalStatusPill from '../components/RentalStatusPill';
import { fulfillmentOptions } from '../utils/rentalStatus';
import { useSearchParams } from 'react-router-dom';

const Rentals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalActive, setModalActive] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [statusSaving, setStatusSaving] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'confirmed',
    customerId: searchParams.get('customerId') || '',
    fulfillmentStatus: searchParams.get('fulfillmentStatus') || '',
  });

  useEffect(() => {
    const status = searchParams.get('status') || 'confirmed';
    const customerId = searchParams.get('customerId') || '';
    const fulfillmentStatus = searchParams.get('fulfillmentStatus') || '';
    setFilters((current) => {
      if (current.status === status && current.customerId === customerId && current.fulfillmentStatus === fulfillmentStatus) return current;
      return { status, customerId, fulfillmentStatus };
    });
    setCurrentPage(1);
  }, [searchParams]);

  const updateFilters = (nextFilters) => {
    setFilters(nextFilters);
    const nextParams = {};
    if (nextFilters.status && nextFilters.status !== 'confirmed') nextParams.status = nextFilters.status;
    if (nextFilters.customerId) nextParams.customerId = nextFilters.customerId;
    if (nextFilters.fulfillmentStatus) nextParams.fulfillmentStatus = nextFilters.fulfillmentStatus;
    setSearchParams(nextParams);
    setCurrentPage(1);
  };

  const fetchRentals = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 10, includeImages: true };
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.customerId) params.customerId = filters.customerId;
      if (filters.fulfillmentStatus) params.fulfillmentStatus = filters.fulfillmentStatus;

      const response = await rentalsAPI.getAll(params);
      setRentals(response.data?.rentals || []);
      setTotalPages(response.data?.totalPages || 1);
    } catch (err) {
      setError('Failed to load rentals');
      setRentals([]);
      setTotalPages(1);
      console.error('Rentals error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    fetchRentals();
  }, [fetchRentals]);

  const handleModalClose = () => {
    setModalActive(false);
  };

  const handleRentalSuccess = () => {
    fetchRentals();
  };

  const handleStatusUpdate = async (rentalId, newStatus) => {
    try {
      setStatusSaving(rentalId);
      const { data } = await rentalsAPI.updateStatus(rentalId, { status: newStatus });
      setRentals((current) => current.map((rental) => rental.id === rentalId ? data : rental));
      await fetchRentals();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update rental status');
    } finally {
      setStatusSaving(null);
    }
  };

  const statusOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Draft', value: 'draft' },
    { label: 'Pending', value: 'pending' },
    { label: 'Confirmed', value: 'confirmed' },
    { label: 'Active', value: 'active' },
    { label: 'Overdue', value: 'overdue' },
    { label: 'Returned', value: 'returned' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  if (loading && (!rentals || rentals.length === 0)) {
    return (
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <InlineStack align="center" gap="400">
              <Spinner size="large" />
              <Text variant="bodyMd">Loading rentals...</Text>
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <InlineStack align="space-between">
          <Text variant="headingLg" as="h1">Rentals</Text>
          <Button onClick={() => setModalActive(true)}>New Rental</Button>
        </InlineStack>
      </div>
      {error && (
        <Layout>
          <Layout.Section>
            <Banner status="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        </Layout>
      )}

      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="400" align="start">
                <div style={{ minWidth: '200px' }}>
                  <Select
                    label="Filter by Status"
                    options={statusOptions}
                    value={filters.status}
                    onChange={(value) => updateFilters({ ...filters, status: value })}
                    placeholder="All statuses"
                  />
                </div>
                <div style={{ minWidth: '200px' }}>
                  <Select label="Filter by Fulfillment" options={[{ label: 'All fulfillment stages', value: '' }, ...fulfillmentOptions.filter((option) => option.value)]} value={filters.fulfillmentStatus} onChange={(value) => updateFilters({ ...filters, fulfillmentStatus: value })} />
                </div>
                {(filters.status !== 'confirmed' || filters.customerId || filters.fulfillmentStatus) && (
                  <div style={{ paddingTop: '24px' }}>
                    <Text variant="bodyMd" tone="subdued">
                      <button
                        onClick={() => updateFilters({ status: 'confirmed', customerId: '', fulfillmentStatus: '' })}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#0066cc', 
                          cursor: 'pointer', 
                          textDecoration: 'underline' 
                        }}
                      >
                        Clear all filters
                      </button>
                    </Text>
                  </div>
                )}
              </InlineStack>
            </BlockStack>

          </Card>
        </Layout.Section>

        <Layout.Section>
          {(rentals && rentals.length > 0) ? (
            <BlockStack gap="400">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', gap: 16 }}>
                {rentals.map((rental) => (
                  <RentalOverviewCard
                    key={rental.id}
                    rental={rental}
                    mode="list"
                    onView={(selected) => {
                      setSelectedRental(selected);
                      setDetailModalOpen(true);
                    }}
                    statusAction={(
                      <RentalStatusPill
                        label={statusOptions.find((option) => option.value === rental.status)?.label || 'Pending'}
                        tone={{ draft: 'info', pending: 'attention', confirmed: 'success', active: 'success', overdue: 'critical', returned: 'success', cancelled: 'warning' }[rental.status] || 'info'}
                        editLabel={`Edit technical status for rental #${rental.id}`}
                        options={statusOptions.filter((option) => option.value !== 'all')}
                        value={rental.status || 'pending'}
                        onChange={(value) => handleStatusUpdate(rental.id, value)}
                        loading={statusSaving === rental.id}
                      />
                    )}
                    fulfillmentAction={<RentalFulfillmentControl rental={rental} onUpdated={fetchRentals} />}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={currentPage > 1}
                    onPrevious={() => setCurrentPage(currentPage - 1)}
                    hasNext={currentPage < totalPages}
                    onNext={() => setCurrentPage(currentPage + 1)}
                    label={`Page ${currentPage} of ${totalPages}`}
                  />
                </InlineStack>
              )}
            </BlockStack>
          ) : (
            <Card>
              <Text variant="bodyMd" tone="subdued">
                No rentals found. Create your first rental to get started.
              </Text>
            </Card>
          )}
        </Layout.Section>
      </Layout>

      <RentalModal
        isOpen={modalActive}
        onClose={handleModalClose}
        onSuccess={handleRentalSuccess}
      />

      <RentalDetailModal
        rental={selectedRental}
        open={detailModalOpen}
        onUpdated={(updated) => { setSelectedRental(updated); fetchRentals(); }}
        onClose={() => setDetailModalOpen(false)}
      />
    </div>
  );
};

export default Rentals;
